import React, { useCallback, useEffect, useMemo, useState } from "react";
import { logActivity } from "../../utils/activity";
import { gmNamesFromConfig } from "../../utils/users";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

function api(path, opts = {}) {
    const token = localStorage.getItem("accessToken");
    return fetch(`${GAMES_CLOUD_BASE}${path}`, {
        ...opts,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
}

// The point rates, grouped as they appear on screen. Keys match the backend's
// scoreSettingKeys; every rate may be negative (minus points).
const RATE_GROUPS = [
    {
        title: "Afslutninger",
        rates: [
            { key: "report_filled", label: "Udfyldt afslutning", hint: "Til den der udfylder rapporten" },
            { key: "report_gm", label: "GM på spil", hint: "Pr. spil. Er der både GM onsite og remote, deler de pointene" },
            { key: "report_host", label: "Vært på spil", hint: "Pr. spil, til vært ved start og vært ved afslutning" },
        ],
    },
    {
        title: "Kundefeedback",
        rates: [
            { key: "feedback_received", label: "Modtaget kundefeedback", hint: "Pr. feedback (også delvise), til spillets GM onsite" },
            { key: "feedback_sat_1", label: "Tilfredshed GM: 1 stjerne", hint: "Til spillets GM onsite" },
            { key: "feedback_sat_2", label: "Tilfredshed GM: 2 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_sat_3", label: "Tilfredshed GM: 3 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_sat_4", label: "Tilfredshed GM: 4 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_sat_5", label: "Tilfredshed GM: 5 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_exp_1", label: "Samlet oplevelse: 1 stjerne", hint: "Til spillets GM onsite" },
            { key: "feedback_exp_2", label: "Samlet oplevelse: 2 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_exp_3", label: "Samlet oplevelse: 3 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_exp_4", label: "Samlet oplevelse: 4 stjerner", hint: "Til spillets GM onsite" },
            { key: "feedback_exp_5", label: "Samlet oplevelse: 5 stjerner", hint: "Til spillets GM onsite" },
        ],
    },
    {
        title: "Mersalg",
        rates: [
            { key: "upsell_drink", label: "Pr. solgt drikkevare", hint: "Antal fra afslutningen, til vært ved afslutning" },
            { key: "upsell_merch_per_100", label: "Pr. 100 kr merchandise", hint: "Hele hundreder, til vært ved afslutning" },
            { key: "upsell_extra_player", label: "Pr. ekstra spiller", hint: "Registreret på ekstra-spiller-siden, til den der registrerer" },
        ],
    },
];

const emptyTeamForm = { id: null, name: "", members: [] };

// ScoreSettings is the "Point" admin tab: the point rates behind the scoreboard plus the
// GM teams whose summed scores it shows. Rates apply to the whole period at view time, so
// a change re-scores retroactively.
export default function ScoreSettings({ config }) {
    const gmNames = useMemo(() => gmNamesFromConfig(config), [config]);

    const [settings, setSettings] = useState(null);
    const [savingRates, setSavingRates] = useState(false);
    const [ratesMsg, setRatesMsg] = useState(null);

    const [teams, setTeams] = useState([]);
    const [teamForm, setTeamForm] = useState(emptyTeamForm);

    const load = useCallback(async () => {
        const [rs, rt] = await Promise.all([api("scoreboard/settings"), api("scoreboard/teams")]);
        if (rs.ok) setSettings((await rs.json()).settings || {});
        if (rt.ok) setTeams((await rt.json()).teams || []);
    }, []);
    useEffect(() => { load(); }, [load]);

    function updateRate(key, value) {
        setRatesMsg(null);
        setSettings(prev => ({ ...prev, [key]: value }));
    }

    async function saveRates(e) {
        e.preventDefault();
        setSavingRates(true);
        setRatesMsg(null);
        try {
            const clean = {};
            for (const g of RATE_GROUPS) for (const rate of g.rates) clean[rate.key] = Number(settings?.[rate.key]) || 0;
            const r = await api("scoreboard/settings", { method: "PUT", body: JSON.stringify({ settings: clean }) });
            if (!r.ok) { setRatesMsg({ ok: false, text: "Kunne ikke gemme satserne." }); return; }
            setSettings((await r.json()).settings || clean);
            setRatesMsg({ ok: true, text: "Satserne er gemt." });
            logActivity("scoreboard_settings_save");
        } catch {
            setRatesMsg({ ok: false, text: "Kunne ikke gemme satserne." });
        } finally {
            setSavingRates(false);
        }
    }

    // A GM belongs to at most one team, so the picker only offers people who are still free.
    // The team being edited keeps its own members listed — including anyone no longer in the
    // staff sheet — so they can be seen and unchecked rather than silently dropped on save.
    const pickableMembers = useMemo(() => {
        const own = new Set(teamForm.members);
        const takenElsewhere = new Set(
            teams.filter(t => t.id !== teamForm.id).flatMap(t => t.members)
        );
        return [...new Set([...gmNames, ...teamForm.members])]
            .filter(n => own.has(n) || !takenElsewhere.has(n))
            .sort((a, b) => a.localeCompare(b, "da"));
    }, [gmNames, teams, teamForm.id, teamForm.members]);

    function toggleMember(name) {
        setTeamForm(f => ({
            ...f,
            members: f.members.includes(name) ? f.members.filter(m => m !== name) : [...f.members, name],
        }));
    }

    async function saveTeam(e) {
        e.preventDefault();
        const name = teamForm.name.trim();
        if (!name) { alert("Teamnavn mangler."); return; }
        const body = JSON.stringify({ name, members: teamForm.members });
        const r = teamForm.id
            ? await api(`scoreboard/teams/${teamForm.id}`, { method: "PUT", body })
            : await api("scoreboard/teams", { method: "POST", body });
        if (!r.ok) {
            // 409 = someone on the list joined another team since this page was loaded.
            const taken = r.status === 409 ? (await r.json().catch(() => ({}))).members : null;
            alert(taken?.length
                ? `${taken.join(", ")} er allerede på et andet team. Opdatér siden og prøv igen.`
                : "Kunne ikke gemme teamet.");
            if (taken?.length) load();
            return;
        }
        logActivity(teamForm.id ? "scoreboard_team_edit" : "scoreboard_team_create", name);
        setTeamForm(emptyTeamForm);
        load();
    }

    async function deleteTeam(t) {
        if (!window.confirm(`Slet teamet "${t.name}"?`)) return;
        const r = await api(`scoreboard/teams/${t.id}`, { method: "DELETE" });
        if (!r.ok) { alert("Kunne ikke slette teamet."); return; }
        logActivity("scoreboard_team_delete", t.name);
        if (teamForm.id === t.id) setTeamForm(emptyTeamForm);
        load();
    }

    return (
        <>
            <section className="acl-card">
                <h2>Pointsatser</h2>
                <p className="acl-hint">
                    Satserne gælder for hele perioden der vises — en ændring slår også igennem bagudrettet.
                    Minuspoint er tilladt.
                </p>
                {settings === null ? (
                    <div className="acl-dim">Henter…</div>
                ) : (
                    <form onSubmit={saveRates}>
                        {RATE_GROUPS.map(group => (
                            <div key={group.title}>
                                <h3>{group.title}</h3>
                                <div className="acl-fields">
                                    {group.rates.map(rate => (
                                        <label key={rate.key} title={rate.hint}>
                                            {rate.label}
                                            <input type="number" step="1" value={settings[rate.key] ?? 0}
                                                onChange={e => updateRate(rate.key, e.target.value)} />
                                            <span className="score-rate-hint">{rate.hint}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="acl-form-actions">
                            <button type="submit" className="accent" disabled={savingRates}>
                                {savingRates ? "Gemmer…" : "Gem satser"}
                            </button>
                            {ratesMsg && (
                                <span className={ratesMsg.ok ? "acl-ok" : "acl-undone"} style={{ alignSelf: "center" }}>
                                    {ratesMsg.text}
                                </span>
                            )}
                        </div>
                    </form>
                )}
            </section>

            <section className="acl-card">
                <h2>Teams</h2>
                <p className="acl-hint">
                    Et team er en gruppe GM'er — teamets score er summen af medlemmernes point.
                    En GM kan kun være på ét team, så dem der allerede er på et, kan ikke vælges igen.
                </p>
                <table className="acl-table">
                    <thead><tr><th>Team</th><th>Medlemmer</th><th></th></tr></thead>
                    <tbody>
                        {teams.length === 0 && <tr><td colSpan={3} className="acl-dim">Ingen teams endnu.</td></tr>}
                        {teams.map(t => (
                            <tr key={t.id}>
                                <td>{t.name}</td>
                                <td className="acl-dim">{t.members.length ? t.members.join(", ") : "—"}</td>
                                <td className="acl-row-actions">
                                    <button onClick={() => setTeamForm({ id: t.id, name: t.name, members: [...t.members] })}>Rediger</button>
                                    <button className="danger" onClick={() => deleteTeam(t)}>Slet</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <form className="acl-form" onSubmit={saveTeam}>
                    <h3>{teamForm.id ? "Rediger team" : "Nyt team"}</h3>
                    <div className="acl-fields">
                        <label className="grow">Teamnavn
                            <input value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} />
                        </label>
                    </div>
                    <div className="acl-weekdays">
                        {pickableMembers.length === 0 && (
                            <span className="acl-dim">Alle GM'er er allerede på et team.</span>
                        )}
                        {pickableMembers.map(name => (
                            <label key={name} className={teamForm.members.includes(name) ? "on" : ""}>
                                <input type="checkbox" checked={teamForm.members.includes(name)}
                                    onChange={() => toggleMember(name)} />
                                {name}
                            </label>
                        ))}
                    </div>
                    <div className="acl-form-actions">
                        <button type="submit" className="accent">{teamForm.id ? "Gem ændringer" : "Opret team"}</button>
                        {teamForm.id && (
                            <button type="button" onClick={() => setTeamForm(emptyTeamForm)}>Annullér</button>
                        )}
                    </div>
                </form>
            </section>
        </>
    );
}
